/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toSortParams } from '@data/dto/api.dto';
import { toCourseType } from '@data/dto/course-type.dto';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import {
  CourseType,
  CourseTypeHttp,
  CourseTypeWizard,
} from '@data/models/course-type.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class CourseTypeService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly courseTypeEndpoint = '/course-type-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listCourseTypes(
    filters: FilterParams,
  ): Promise<PaginateResult<CourseType[]>> {
    const { data: response, error } = await this.API?.get<{
      data: CourseTypeHttp[];
      meta: Meta;
    }>(this.courseTypeEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toCourseType) || [],
      totalSize: response?.meta.page.total || 0,
    };
  }

  public async upsertCourseType({
    id,
    ...courseTypeWizard
  }: CourseTypeWizard): Promise<CourseType | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<CourseTypeWizard, 'id'> },
        { data: CourseTypeHttp }
      >(this.courseTypeEndpoint, { data: courseTypeWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toCourseType(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: CourseTypeWizard },
      { data: CourseTypeHttp }
    >(`${this.courseTypeEndpoint}/${id}`, {
      data: { ...courseTypeWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toCourseType(data?.data || {});
  }

  public async deleteCourseType(id: string): Promise<CourseType | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: CourseTypeHttp;
    }>(`${this.courseTypeEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toCourseType(data?.data || {});
  }

  public async sortCourseType(
    sortParams: DatatableReorderParams<CourseType>,
  ): Promise<CourseType | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.courseTypeEndpoint}/${id}/sort`,
      {
        data: toSortParams('course_type_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
